import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "react-bootstrap";
import ReactTable from "react-table-6";
import ReactTablePagination from "../../../custom/growth/ProductsCVTablePagination";
import { provisionArray } from "../../../../utils/provisonArray";
import { AD_TYPES, SD_CAMPAIGN_TYPE_OPTIONS } from "./constants";
import { useDebounce } from "@reactuses/core";
import { useTargetableEntities } from "./useTargetableEntities";
import Loading from "../Loading";
import { Switch } from "../../../fields/base/Switch";
import { ReviewBadge } from "./SDTargetingOptions";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { Grid } from "../../../../../styled-system/jsx";
import { Checkbox } from "../../../fields/base/Checkbox";
import TagsReviewDisplay from "../TagsReviewDisplay";
import { css } from "../../../../../styled-system/css";

const EMPTY_ARRAY = [];

const SDAudienceTargets = ({ review, state, onChange, isModal }) => {
  const { t } = useTranslation(["advertising"]);
  const [audienceSearchFilter, setAudienceSearchFilter] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [customAudiencesFilter, setCustomAudiencesFilter] = useState(false);

  const debouncedAudienceSearchFilter = useDebounce(audienceSearchFilter, 300);
  const hasAMCModule = useModuleEnabled("AMC");

  const toggleAudienceTargets = (audience) => {
    let updatedAudiences;

    if (state.filledTargetAudiences?.map((audience) => audience.value).includes(audience.value)) {
      updatedAudiences = state.filledTargetAudiences.filter((a) => a.value !== audience.value);
    } else {
      updatedAudiences = [...state.filledTargetAudiences, { label: audience.label, value: audience.value }];
    }

    onChange({ filledTargetAudiences: updatedAudiences });
  };

  const getAudienceTargets = useTargetableEntities(debouncedAudienceSearchFilter, state.adType, (res) => {
    setSearchResults((prevResults) => [...prevResults, ...(res.data?.targetableEntities ?? [])]);
  });

  useEffect(() => {
    if (debouncedAudienceSearchFilter) {
      setSearchResults([]);
      getAudienceTargets.mutate();
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAudienceSearchFilter]);

  const formattedResults = useMemo(() => {
    let results = searchResults?.map((result) => ({
      label: result.audienceResolved,
      value: result.audienceId,
      isCustom: result.path.join("").toLowerCase().includes("custom"),
    }));

    if (customAudiencesFilter) {
      results = results?.filter((result) => {
        return result.isCustom;
      });
    }

    return results;
  }, [searchResults, customAudiencesFilter]);

  const audienceData = provisionArray(formattedResults, 2);

  if (
    ![AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(state.adType) ||
    state.sdCampaignType !== SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value
  ) {
    return;
  }

  if (review && !state.savedAdPlan?.filledTargetAudiences?.length && !state.filledTargetAudiences?.length && !isModal) {
    return null;
  }

  return (
    <div className="mt-5">
      <h3
        style={{
          fontWeight: 560,
          fontSize: "1.6rem",
          color: "#73706E",
        }}
      >
        {hasAMCModule ?
          t(($) => $.advertising["AMC, Lifestyle, and In-Market Audiences:"])
        : t(($) => $.advertising["Lifestyle and In-Market Audiences:"])}
      </h3>

      {review ?
        <div
          className={css({
            display: "flex",
            flexWrap: "wrap",
            gap: "small",
          })}
        >
          {state.id && !isModal ?
            state.savedAdPlan?.filledTargetAudiences?.map((savedAudience) => (
              <ReviewBadge text={savedAudience.label} key={savedAudience.value} />
            ))
          : state.filledTargetAudiences?.map((savedAudience) => (
              <ReviewBadge text={savedAudience.label} key={savedAudience.value} />
            ))
          }
        </div>
      : <>
          {state.filledTargetAudiences?.length ?
            <div className="pt-2 pb-4">
              <TagsReviewDisplay
                tags={state.filledTargetAudiences.map((audience) => audience.label)}
                onRemove={(tagToRemove) => {
                  if (
                    state.id &&
                    state.savedAdPlan?.filledTargetAudiences
                      ?.map((savedAudience) => savedAudience.label)
                      ?.some((savedAudienceName) => savedAudienceName === tagToRemove)
                  ) {
                    return;
                  }

                  const updatedAudiences = state.filledTargetAudiences?.filter(
                    (audience) => audience.label !== tagToRemove,
                  );

                  onChange({ filledTargetAudiences: updatedAudiences });
                }}
                readOnly={false}
              />
            </div>
          : null}

          <ReactTable
            showPaginationTop={true}
            showPaginationBottom={false}
            showPageSizeOptions={false}
            defaultPageSize={5}
            // Below is needed to eliminate the no data message that covers table headers if no data to render
            NoDataComponent={() => (
              <Grid>
                {getAudienceTargets.isLoading ?
                  <Loading height={"200px"} />
                : audienceSearchFilter && !audienceData?.length ?
                  <p
                    className="text-center"
                    style={{
                      color: "#73706E",
                      fontWeight: "700",
                      marginTop: "10px",
                    }}
                  >
                    {customAudiencesFilter ?
                      t(($) => $.advertising["No custom audiences found"])
                    : t(($) => $.advertising["No audiences found"])}
                  </p>
                : null}
              </Grid>
            )}
            minRows={0}
            columns={[
              {
                id: "select",
                Header: "",
                style: {
                  textAlign: "center",
                  fontWeight: 500,
                  minHeight: "60px",
                },
                Cell: (props) => {
                  const audience = props.original?.[0];
                  const { value: audienceId } = audience ?? {};

                  return (
                    <Checkbox
                      value={state.filledTargetAudiences
                        ?.map((selectedAudience) => selectedAudience.value)
                        .includes(audienceId)}
                      isDisabled={
                        review ||
                        state.savedAdPlan?.filledTargetAudiences
                          ?.map((selectedAudience) => selectedAudience.value)
                          .includes(audienceId)
                      }
                      onChange={() => {
                        toggleAudienceTargets(audience);
                      }}
                    />
                  );
                },
                width: 60,
                accessor: "metric",
                filterMethod: () => true,
                Filter: () => null,
              },
              {
                id: "category",
                Header: "",
                style: {
                  textAlign: "left",
                  color: "rgb(115, 112, 110)",
                  cursor: "pointer",
                },
                Cell: (props) => {
                  const audience = props.original?.[0] ?? {};
                  const { label, isCustom } = audience ?? {};

                  return (
                    <div
                      onClick={() => {
                        toggleAudienceTargets(audience);
                      }}
                    >
                      {label}
                      {isCustom ?
                        <>
                          <br />
                          <Badge
                            className={`badge bg-trellis-purple w-auto mt-2`}
                            style={{
                              borderRadius: "5px",
                            }}
                          >
                            {t(($) => $.advertising["Custom Audience"])}
                          </Badge>
                        </>
                      : null}
                    </div>
                  );
                },
                accessor: "[0].name",
                filterMethod: () => true,
                Filter: () => null,
              },
              {
                id: "select",
                Header: "",
                headerClassName: "wordwrap",
                style: {
                  textAlign: "center",
                  fontWeight: 500,
                  minHeight: "60px",
                },
                Cell: (props) => {
                  const audience = props.original?.[1];

                  const { value: audienceId } = audience ?? {};
                  // In case of filtered recommendations, dont render checkbox if odd number of filter results
                  if (!audienceId) {
                    return null;
                  }
                  return (
                    <Checkbox
                      value={state.filledTargetAudiences
                        ?.map((selectedAudience) => selectedAudience.value)
                        .includes(audienceId)}
                      isDisabled={
                        review ||
                        state.savedAdPlan?.filledTargetAudiences
                          ?.map((selectedAudience) => selectedAudience.value)
                          .includes(audienceId)
                      }
                      onChange={() => {
                        toggleAudienceTargets(audience);
                      }}
                    />
                  );
                },
                width: 60,
                accessor: "metric",
                filterMethod: () => true,
                Filter: () => null,
              },
              {
                id: "category",
                Header: "",
                headerClassName: "wordwrap",
                style: {
                  textAlign: "left",
                  color: "rgb(115, 112, 110)",
                  cursor: "pointer",
                },
                Cell: (props) => {
                  const audience = props.original?.[1];
                  const { label, isCustom } = audience ?? {};

                  return (
                    <div
                      onClick={() => {
                        toggleAudienceTargets(audience);
                      }}
                    >
                      {label}
                      {isCustom ?
                        <>
                          <br />
                          <Badge
                            className={`badge bg-trellis-purple w-auto mt-2`}
                            style={{
                              borderRadius: "5px",
                            }}
                          >
                            {t(($) => $.advertising["Custom Audience"])}
                          </Badge>
                        </>
                      : null}
                    </div>
                  );
                },
                accessor: "[1].name",
                filterMethod: () => true,
                Filter: () => null,
              },
            ]}
            PaginationComponent={ReactTablePagination}
            getPaginationProps={() => ({
              updateSearch: setAudienceSearchFilter,
              clearFilters: () => {
                setAudienceSearchFilter("");
              },
              hideResultsCount: true,
              hideFilters: true,
              clearSearchTermFilters: () => setAudienceSearchFilter(""),
              searchTerm: audienceSearchFilter,
              table: "recommended-category-options",
              style: { marginBottom: "1rem" },
              carouselLayout: false,
              disableSearchFocus: true,
              searchWidth: "280px",
              additionalFilters:
                hasAMCModule ?
                  <div className="pt-2 d-flex justify-content-end align-items-center gap-3 fs-6">
                    <span>{t(($) => $.advertising["All Audiences"])}</span>
                    <Switch
                      onChange={() => {
                        setCustomAudiencesFilter(!customAudiencesFilter);
                      }}
                      isSelected={customAudiencesFilter}
                    />
                    <span>{t(($) => $.advertising["Custom Audiences"])}</span>
                  </div>
                : null,
            })}
            style={{
              borderRadius: "15px",
              border: "none",
              boxShadow: "none",
            }}
            data={audienceData ?? EMPTY_ARRAY}
            filterable={false}
            sortable={false}
          />
        </>
      }
    </div>
  );
};

export default SDAudienceTargets;
